import { History, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import * as React from 'react';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { TelaProtegidaCarregando } from '@/components/layout/tela-carregando';
import { EstadoVazio } from '@/components/patterns/states';
import { useSessao } from '@/hooks/use-sessao';
import { CAPABILITY_SYSADMIN, podeAdministrar } from '@/lib/capabilities';
import { cn } from '@/lib/utils';
import { PainelAuditoria } from './PainelAuditoria';
import { PainelCapabilities } from './PainelCapabilities';

/**
 * Página "Usuários" (/usuarios): permissionamento. Duas abas — Permissões
 * (concede/revoga capabilities) e Auditoria. Substitui o antigo modal
 * "Administração". Sem `admin`/`sysadmin` mostra o estado vazio explicando a
 * capability, sem redirecionar.
 */

type AbaId = 'permissoes' | 'auditoria';

const ABAS: Array<{ id: AbaId; rotulo: string; icone: LucideIcon }> = [
  { id: 'permissoes', rotulo: 'Permissões', icone: Users },
  { id: 'auditoria', rotulo: 'Auditoria', icone: History },
];

export function UsuariosPage() {
  const { roles, rolesCarregando } = useSessao();
  const [aba, setAba] = React.useState<AbaId>('permissoes');
  const permitido = podeAdministrar(roles).permitido;

  if (rolesCarregando) {
    return (
      <TelaProtegidaCarregando
        titulo="Usuários"
        descricao="Quem pode homologar, publicar no painel do cliente e administrar, e o histórico das mudanças."
      />
    );
  }

  return (
    <PageBody>
      <PageHeader
        titulo="Usuários"
        descricao="Quem pode homologar, publicar no painel do cliente e administrar, e o histórico das mudanças."
      />

      {!permitido ? (
        <EstadoVazio
          titulo="Sem acesso a Usuários"
          descricao='Esta tela exige a capability "admin". Peça a alguém com admin para conceder.'
        />
      ) : (
        <div className="flex flex-col gap-5">
          <div role="tablist" aria-label="Seções de Usuários" className="flex gap-1 border-b border-border">
            {ABAS.map((item) => {
              const Icone = item.icone;
              const ativa = aba === item.id;
              return (
                <button
                  key={item.id}
                  id={`usuarios-aba-${item.id}`}
                  type="button"
                  role="tab"
                  aria-selected={ativa}
                  aria-controls="usuarios-painel"
                  onClick={() => setAba(item.id)}
                  className={cn(
                    '-mb-px flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-body transition-colors duration-fast ease-standard',
                    ativa
                      ? 'border-primary font-bold text-primary'
                      : 'border-transparent text-on-surface-muted hover:text-on-surface',
                  )}
                >
                  <Icone className="size-4 shrink-0" aria-hidden="true" />
                  {item.rotulo}
                </button>
              );
            })}
          </div>

          <div id="usuarios-painel" role="tabpanel" aria-labelledby={`usuarios-aba-${aba}`}>
            {aba === 'permissoes' ? (
              <PainelCapabilities ehSysadmin={roles.includes(CAPABILITY_SYSADMIN)} />
            ) : (
              <PainelAuditoria />
            )}
          </div>
        </div>
      )}
    </PageBody>
  );
}
